import { AlertType, Contact, ContactUsPaginatedResponse } from '@types';
import { AlertModal, Card, PageHeader, Pagination, SearchBar } from '_atoms';
import { AdminService } from '_services';
import React, { useCallback, useEffect, useState } from 'react';
import {
  MdCheckCircle,
  MdEmail,
  MdExpandLess,
  MdExpandMore,
  MdMessage,
  MdPerson,
  MdSchedule,
} from 'react-icons/md';

const ContactUsMessagesPage: React.FC = () => {
  const [messages, setMessages] = useState<Contact[]>([]);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 20,
    totalRecords: 0,
    totalPages: 0,
    hasPrevious: false,
    hasNext: false,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [markingAsRead, setMarkingAsRead] = useState<Set<string>>(new Set());
  const [replyingTo, setReplyingTo] = useState<Set<string>>(new Set());
  const [replyText, setReplyText] = useState('');
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(
    null,
  );
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(
    new Set(),
  );
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: AlertType;
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  const showAlert = (type: AlertType, title: string, message: string) => {
    setAlertModal({
      isOpen: true,
      type,
      title,
      message,
    });
  };

  const closeAlert = () => {
    setAlertModal(prev => ({ ...prev, isOpen: false }));
  };

  const loadMessages = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = (await AdminService.getContactUsMessages(
        pagination.pageNumber,
        pagination.pageSize,
        searchTerm,
      )) as unknown as ContactUsPaginatedResponse;

      setMessages(response.messages);
      setPagination({
        pageNumber: response.pagination.pageNumber,
        pageSize: response.pagination.pageSize,
        totalRecords: response.pagination.totalRecords,
        totalPages: response.pagination.totalPages,
        hasPrevious: response.pagination.hasPrevious,
        hasNext: response.pagination.hasNext,
      });
    } catch (error) {
      console.error('Error loading contact us messages:', error);
      showAlert('error', 'Error', 'Failed to load contact us messages.');
    } finally {
      setIsLoading(false);
      setIsLoadingInitial(false);
    }
  }, [pagination.pageNumber, pagination.pageSize, searchTerm]);

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, pageNumber: page }));
  };

  const handlePageSizeChange = (pageSize: number) => {
    setPagination(prev => ({ ...prev, pageSize, pageNumber: 1 }));
  };

  const handleSearch = useCallback((search: string) => {
    setSearchTerm(search);
    setPagination(prev => ({ ...prev, pageNumber: 1 }));
  }, []);

  const handleReload = () => {
    loadMessages();
  };

  const handleMarkAsRead = async (messageId: string) => {
    setMarkingAsRead(prev => new Set(prev).add(messageId));

    try {
      await AdminService.markContactMessageAsRead(messageId);

      // Update the local state to reflect the change
      setMessages(prev =>
        prev.map(message =>
          message.id === messageId ? { ...message, isRead: true } : message,
        ),
      );

      showAlert('success', 'Success', 'Message marked as read successfully!');
    } catch (error) {
      console.error('Error marking message as read:', error);
      showAlert(
        'error',
        'Error',
        'Failed to mark message as read. Please try again.',
      );
    } finally {
      setMarkingAsRead(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });
    }
  };

  const handleReply = (messageId: string) => {
    const message = messages.find(msg => msg.id === messageId);
    if (message) {
      setSelectedMessageId(messageId);
      setReplyText('');
    }
  };

  const handleSendReply = async () => {
    if (!selectedMessageId || !replyText.trim()) {
      showAlert('error', 'Error', 'Please enter a reply message.');
      return;
    }

    setReplyingTo(prev => new Set(prev).add(selectedMessageId));

    try {
      await AdminService.replyToContactMessage(
        selectedMessageId,
        replyText.trim(),
      );

      // Mark the message as read after replying
      setMessages(prev =>
        prev.map(message =>
          message.id === selectedMessageId
            ? { ...message, isRead: true }
            : message,
        ),
      );

      showAlert('success', 'Success', 'Reply sent successfully!');
      setSelectedMessageId(null);
      setReplyText('');
    } catch (error) {
      console.error('Error sending reply:', error);
      showAlert('error', 'Error', 'Failed to send reply. Please try again.');
    } finally {
      setReplyingTo(prev => {
        const newSet = new Set(prev);
        newSet.delete(selectedMessageId);
        return newSet;
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const toggleMessageExpansion = (messageId: string) => {
    setExpandedMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  const formatReplyDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getCategoryIcon = (category: string) => {
    switch (category?.toLowerCase()) {
      case 'general':
        return <MdMessage className="w-5 h-5 text-blue-600" />;
      case 'support':
        return <MdEmail className="w-5 h-5 text-green-600" />;
      case 'feedback':
        return <MdMessage className="w-5 h-5 text-purple-600" />;
      default:
        return <MdMessage className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusBadge = (isRead: boolean) => {
    return (
      <span
        className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm ${
          isRead
            ? 'bg-green-100 text-green-800 border border-green-200'
            : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
        }`}
      >
        <div
          className={`w-2 h-2 rounded-full mr-2 ${
            isRead ? 'bg-green-500' : 'bg-yellow-500'
          }`}
        ></div>
        {isRead ? 'Read' : 'Unread'}
      </span>
    );
  };

  // Load messages on page load and when dependencies change
  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        icon={MdEmail}
        title="Contact Us Messages"
        description="View and manage contact us messages from customers."
        reload={handleReload}
      />

      {/* Search Bar */}
      <SearchBar
        placeholder="Search by name, email, or message content..."
        onSearch={handleSearch}
        className="mb-4"
      />

      {/* Main Content */}
      {isLoadingInitial ? (
        <Card>
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading messages...</p>
            </div>
          </div>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Messages Table */}
          <Card>
            <div className="overflow-x-auto">
              {messages.length === 0 ? (
                <div className="text-center py-8">
                  <MdEmail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">
                    {searchTerm
                      ? 'No messages found matching your search criteria.'
                      : 'No contact us messages found.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map(message => (
                    <div
                      key={message.id}
                      className={`border border-gray-200 rounded-lg overflow-hidden ${
                        !message.isRead ? 'bg-blue-50' : 'bg-white'
                      }`}
                    >
                      {/* Main Message Row */}
                      <div className="p-6">
                        <div className="flex items-start ">
                          {/* Contact Info */}
                          <div className="flex flex-col w-full">
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0">
                                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center shadow-sm">
                                  <MdPerson className="w-7 h-7 text-blue-700" />
                                </div>
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-semibold text-gray-900 truncate">
                                  {message.name}
                                </div>
                                <div className="text-sm text-gray-500 truncate">
                                  {message.email}
                                </div>
                                {/* Date */}
                                <div className="flex items-center justify-start text-sm text-gray-500">
                                  <MdSchedule className="w-4 h-4 mr-2 text-gray-400" />
                                  <span className="font-medium">
                                    {formatDate(message.createdAt)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center justify-between">
                            {/* Category */}
                            <div>
                              <div className="flex items-center">
                                <div className="flex items-center px-3 py-1.5 bg-gray-50 rounded-full border border-gray-200">
                                  {getCategoryIcon(message.category)}
                                  <span className="ml-2 text-sm font-medium text-gray-700 capitalize">
                                    {message.category}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Status */}
                            <div>
                              <div className="flex justify-center">
                                {getStatusBadge(message.isRead)}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Message */}
                        <div className="lg:col-span-4 mt-2">
                          <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                            <div className="text-sm text-gray-800 leading-relaxed">
                              <div className="line-clamp-3">
                                {message.message}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Actions Row */}
                        <div className="mt-4 flex items-center justify-between">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleReply(message.id)}
                              disabled={replyingTo.has(message.id)}
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                              <MdEmail className="w-3 h-3 mr-1" />
                              {replyingTo.has(message.id)
                                ? 'Sending...'
                                : 'Reply'}
                            </button>
                            {!message.isRead && (
                              <button
                                onClick={() => handleMarkAsRead(message.id)}
                                disabled={markingAsRead.has(message.id)}
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                              >
                                <MdCheckCircle className="w-3 h-3 mr-1" />
                                {markingAsRead.has(message.id)
                                  ? 'Marking...'
                                  : 'Mark as Read'}
                              </button>
                            )}
                          </div>

                          {/* Expand/Collapse Replies */}
                          {message.replies && message.replies.length > 0 && (
                            <button
                              onClick={() => toggleMessageExpansion(message.id)}
                              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                            >
                              {expandedMessages.has(message.id) ? (
                                <>
                                  <MdExpandLess className="w-3 h-3 mr-1" />
                                  Hide Replies ({message.replies.length})
                                </>
                              ) : (
                                <>
                                  <MdExpandMore className="w-3 h-3 mr-1" />
                                  Show Replies ({message.replies.length})
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Replies Section */}
                      {message.replies &&
                        message.replies.length > 0 &&
                        expandedMessages.has(message.id) && (
                          <div className="border-t border-gray-200 bg-gray-50">
                            <div className="p-4">
                              <h4 className="text-sm font-medium text-gray-900 mb-3">
                                Replies ({message.replies.length})
                              </h4>
                              <div className="space-y-3">
                                {message.replies.map((reply, index) => (
                                  <div
                                    key={reply.id}
                                    className="bg-white rounded-lg p-4 border border-gray-200"
                                  >
                                    <div className="flex items-start justify-between mb-2">
                                      <div className="flex items-center">
                                        <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mr-2">
                                          <MdEmail className="w-3 h-3 text-green-600" />
                                        </div>
                                        <span className="text-sm font-medium text-gray-900">
                                          Admin Reply
                                        </span>
                                      </div>
                                      <span className="text-xs text-gray-500">
                                        {formatReplyDate(reply.createdAt)}
                                      </span>
                                    </div>
                                    <div className="text-sm text-gray-700 ml-8">
                                      {reply.replyMessage}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>

          {/* Pagination */}
          {messages.length > 0 && (
            <Pagination
              pageNumber={pagination.pageNumber}
              totalPages={pagination.totalPages}
              pageSize={pagination.pageSize}
              hasNext={pagination.hasNext}
              hasPrevious={pagination.hasPrevious}
              loading={isLoading}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              onReload={handleReload}
            />
          )}

          {/* Summary */}
          {messages.length > 0 && (
            <div className="text-sm text-gray-600 text-center">
              Showing {messages.length} of {pagination.totalRecords} messages
            </div>
          )}
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={closeAlert}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />

      {/* Reply Modal */}
      {selectedMessageId && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Reply to Message
              </h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Reply:
                </label>
                <textarea
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={4}
                  placeholder="Enter your reply message..."
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setSelectedMessageId(null);
                    setReplyText('');
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendReply}
                  disabled={
                    !replyText.trim() || replyingTo.has(selectedMessageId)
                  }
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {replyingTo.has(selectedMessageId)
                    ? 'Sending...'
                    : 'Send Reply'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactUsMessagesPage;
